import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, Copy, QrCode } from "lucide-react";
import { PageHeader } from "../../../components/common";
import { Button } from "../../../components/button";

const QRScannerTutorial = () => {
  const [copied, setCopied] = useState(false);
  const navigate = useNavigate();
  const qrScannerUrl = `${window.location.hostname}/qr-scanner`;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(qrScannerUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
    } catch (err) {
      console.error("Clipboard copy failed:", err);
    }
  };

  return (
    <div className="p-6 dark:bg-gray-900 min-h-screen transition-colors duration-300">
      <div className="mx-auto space-y-6">
        <PageHeader
          title="Access QR Scanner"
          subtitle="Learn how to quickly access the QR scanner from your phone or tablet to check in citizens at events."
          actions={
            <Button
              onClick={() => navigate("/qr-scanner/welcome")}
              className="flex items-center gap-2"
            >
              <QrCode className="h-4 w-4" />
              Scan QR
            </Button>
          }
        />

        {/* Step 1 */}
        <div className="relative border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 rounded-xl p-5 shadow-sm transition-colors duration-300">
          <div className="flex items-center gap-4">
            <div className="flex items-center justify-center w-8 h-8 rounded-full bg-[#F8FDFC] dark:bg-gray-700 text-[#00C49A] font-semibold">
              1
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-gray-900 dark:text-gray-100">
                Open the admin login page on your phone or tablet
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                Use your mobile browser (Safari, Chrome, etc.) to visit the admin login page.
              </p>
            </div>
          </div>

          {/* Copy URL button */}
          <div className="absolute top-5 right-5">
            <button
              onClick={handleCopy}
              className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium rounded-lg bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-600 transition"
              style={{ border: '1px solid var(--primary-color, hsla(167, 100%, 38%, 1))' }}
            >
              {copied ? (
                <>
                  <Check size={12} />
                  Copied
                </>
              ) : (
                <>
                  <Copy size={12} />
                  Copy URL
                </>
              )}
            </button>
          </div>
        </div>

        {/* Step 2 */}
        <div className="border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 rounded-xl p-5 shadow-sm transition-colors duration-300">
          <div className="flex items-center gap-4">
            <div className="flex items-center justify-center w-8 h-8 rounded-full bg-[#F8FDFC] dark:bg-gray-700 text-[#00C49A] font-semibold">
              2
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-gray-900 dark:text-gray-100">
                Enter your admin credentials
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                Type in your admin email and password. Your login credentials are
                the same across all devices.
              </p>
            </div>
          </div>
        </div>

        {/* Step 3 */}
        <div className="border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 rounded-xl p-5 shadow-sm transition-colors duration-300">
          <div className="flex items-center gap-4">
            <div className="flex items-center justify-center w-8 h-8 rounded-full bg-[#F8FDFC] dark:bg-gray-700 text-[#00C49A] font-semibold">
              3
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-gray-900 dark:text-gray-100">
                You&apos;ll be redirected to the QR Scanner
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                Once you log in from mobile, the system automatically takes you
                to the QR scanner interface instead of the full dashboard.
              </p>
            </div>
          </div>
        </div>

        {/* Step 4 */}
        <div className="border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 rounded-xl p-5 shadow-sm transition-colors duration-300">
          <div className="flex items-center gap-4">
            <div className="flex items-center justify-center w-8 h-8 rounded-full bg-[#F8FDFC] dark:bg-gray-700 text-[#00C49A] font-semibold">
              4
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-gray-900 dark:text-gray-100">
                Start scanning citizens!
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                Allow camera access when prompted, then point your phone at
                citizens&apos; QR codes to check them in.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QRScannerTutorial;
